import { AbstractMentionResource, type MentionProvider } from '@atlaskit/mention/resource';
import type { MentionDisabledState, MentionDisabledStateInput } from '@atlaskit/mention/types';
import {
	type MentionDescription,
	type MentionsResult,
	type MentionStats,
} from '@atlaskit/mention/types';
import { type Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { agentToMentionDescription } from '../../utils/agent-to-mention-description';

const MAX_RECOMMENDED_AGENT_RESULTS = 5;

/** Getter for the current recommended-agent list. Function form so consumers can plumb via a ref. */
type RecommendedAgentsGetter = () => readonly Agent[];

/** Minimal shape passed back when the user picks an agent row. */
export type SelectedAgentRef = {
	id: string;
	name: string;
};

/** Fired when the user picks an agent row in the typeahead. */
type OnAgentSelected = (agent: SelectedAgentRef) => void;

/** Getter for the active agent ids (typically a one-element array). */
type SelectedAgentIdsGetter = () => readonly string[];

type WithAgentSupportOptions = {
	/** Agents to prepend on empty-query (preloaded from URS recommendations). */
	getRecommendedAgents: RecommendedAgentsGetter;
	/** Fires when the user picks an agent row. */
	onAgentSelected?: OnAgentSelected;
	/**
	 * Getter for the conversation's currently-active agent ids. Function form
	 * so the predicate reads the freshest store state each time it's asked,
	 * without rebuilding the provider when the selection changes.
	 *
	 * When omitted (or returns an empty array), no chip is disabled.
	 */
	getSelectedAgentIds?: SelectedAgentIdsGetter;
	/**
	 * Tooltip rendered on disabled (non-active) agent mention chips.
	 *
	 * Must be a translated string sourced from the consumer's `react-intl`
	 * catalogue — the provider intentionally has no English default so a
	 * missing translation cannot leak into production. When omitted, the
	 * disabled-state predicate is a no-op (chips render as default) so
	 * consumers that haven't wired translations get graceful degradation
	 * instead of an untranslated tooltip.
	 */
	disabledAgentTooltip?: string;
	/**
	 * Fires when a mention chip is destroyed in the editor (removed from the
	 * doc by any means — backspace, select-and-delete, programmatic replace,
	 * editor unmount). Only forwarded to the consumer when the destroyed id
	 * matches an entry in `getSelectedAgentIds()` (i.e. it's a known agent),
	 * so consumers don't have to discriminate themselves.
	 *
	 * Use this to keep `selectedAgentIds` in sync with the chips actually
	 * present in the editor.
	 */
	onAgentMentionDestroyed?: (agentId: string) => void;
};

/** Internal key used for the wrapper's subscription on the inner provider. */
const INNER_SUBSCRIPTION_KEY = 'rovo-agent-mention-decorator';

/**
 * Factory that decorates a `MentionProvider` so it surfaces Rovo agents in
 * the @-mention typeahead alongside the people the inner provider already
 * returns.
 *
 * - **Empty query**: prepends the consumer-supplied recommended agents to
 *   whatever the inner provider emits (which is people-only for empty
 *   queries from URS).
 * - **Typed query**: relies on the inner provider to return agents from
 *   URS in the same response as people. Agent rows are discriminated by
 *   `appType === 'agent'`. No client-side overlay or second round-trip.
 *
 * The decorator composes the inner provider rather than extending it so the
 * inner retains full ownership of people search, debouncing, and analytics.
 *
 * Consumers MUST call `.dispose()` on the returned instance when discarding
 * the wrapper (e.g. from a `useEffect` cleanup) so the inner provider
 * releases its reference to the wrapper's subscription callback. Prefer
 * `useChatMentionResource` which owns this lifecycle for you.
 */
export const withAgentSupport = (
	innerProvider: MentionProvider,
	options: WithAgentSupportOptions,
): AgentAwareMentionProvider => new AgentAwareMentionProvider(innerProvider, options);

/**
 * Mention provider that merges Rovo agents into typeahead results. Returned
 * by `withAgentSupport`. Implements `MentionProvider` so it can be passed
 * directly to the editor; adds `dispose()` for explicit cleanup of the
 * subscription it holds on the inner provider.
 */
export class AgentAwareMentionProvider extends AbstractMentionResource {
	private readonly innerProvider: MentionProvider;
	private readonly getRecommendedAgents: RecommendedAgentsGetter;
	private readonly onAgentSelected?: OnAgentSelected;
	private readonly getSelectedAgentIds?: SelectedAgentIdsGetter;
	private readonly disabledAgentTooltip: string | undefined;
	private readonly onAgentMentionDestroyed?: (agentId: string) => void;
	/**
	 * Listeners registered via `subscribeToDisabledStateChanges`. Each
	 * mention `NodeView` adds itself here and is notified whenever the
	 * active agent selection changes, so already-rendered chips re-evaluate
	 * their disabled state.
	 */
	private readonly disabledStateListeners = new Set<() => void>();

	constructor(innerProvider: MentionProvider, options: WithAgentSupportOptions) {
		super();
		this.innerProvider = innerProvider;
		this.getRecommendedAgents = options.getRecommendedAgents;
		this.onAgentSelected = options.onAgentSelected;
		this.getSelectedAgentIds = options.getSelectedAgentIds;
		this.disabledAgentTooltip = options.disabledAgentTooltip;
		this.onAgentMentionDestroyed = options.onAgentMentionDestroyed;

		this.innerProvider.subscribe(
			INNER_SUBSCRIPTION_KEY,
			(mentions, query, stats) => this.emitMerged(mentions, query, stats),
			undefined,
			undefined,
			undefined,
			undefined,
		);
	}

	/**
	 * Releases this wrapper's subscription on the inner provider. Call once
	 * before discarding the wrapper instance so the inner provider can drop
	 * its reference to the closure-bound callback.
	 */
	dispose(): void {
		this.innerProvider.unsubscribe(INNER_SUBSCRIPTION_KEY);
		this.disabledStateListeners.clear();
	}

	filter(query?: string): void {
		this.innerProvider.filter(query);
	}

	isFiltering(query: string): boolean {
		return this.innerProvider.isFiltering(query);
	}

	shouldHighlightMention(mention: MentionDescription): boolean {
		return this.innerProvider.shouldHighlightMention?.(mention) ?? false;
	}

	recordMentionSelection(mention: MentionDescription): void {
		// Always notify the inner provider so people-row analytics / recent-pick keep working.
		this.innerProvider.recordMentionSelection?.(mention);

		if (!isAgentMention(mention) || !this.onAgentSelected) {
			return;
		}
		this.onAgentSelected({ id: mention.id, name: mention.name ?? '' });
	}

	/**
	 * Subscribe to disabled-state changes. Called by `MentionNodeView` (via
	 * the `MentionProvider` interface) so each already-rendered chip can
	 * re-evaluate when the active agent set changes.
	 */
	subscribeToDisabledStateChanges(listener: () => void): () => void {
		this.disabledStateListeners.add(listener);
		return () => {
			this.disabledStateListeners.delete(listener);
		};
	}

	/**
	 * Called by the editor NodeView when a mention chip is removed from the
	 * doc. We forward to the consumer's `onAgentMentionDestroyed` only when
	 * the destroyed id is a known agent (i.e. present in the current
	 * selection history). People mentions are silently ignored so consumers
	 * don't need to discriminate.
	 */
	notifyMentionDestroyed(mention: { id: string }): void {
		const ids = this.getSelectedAgentIds?.() ?? [];
		if (!ids.includes(mention.id)) {
			return;
		}
		this.onAgentMentionDestroyed?.(mention.id);
	}

	/**
	 * Notify all subscribers that the disabled-state predicate inputs have
	 * changed (typically because `selectedAgentIds` changed in the chat
	 * store). Triggers each subscribed `MentionNodeView` to re-run its
	 * `updateState`.
	 */
	notifyDisabledStateChanged(): void {
		this.disabledStateListeners.forEach((listener) => listener());
	}

	/**
	 * Returns the disabled visual state for a mention chip.
	 *
	 * `selectedAgentIds` is treated as an ordered history of every agent the
	 * user has made active (most-recently-active last). A chip is disabled iff
	 * its id appears anywhere in that history but is NOT the last (i.e. most
	 * recently active) entry. People mentions are never in the history, so
	 * they are never disabled.
	 */
	getMentionDisabledState(mention: MentionDisabledStateInput): MentionDisabledState | undefined {
		const ids = this.getSelectedAgentIds?.() ?? [];
		if (!ids.includes(mention.id)) {
			return undefined;
		}
		if (ids.at(-1) === mention.id) {
			return undefined;
		}

		return { disabled: true, tooltip: this.disabledAgentTooltip };
	}

	private emitMerged(
		innerMentions: MentionDescription[],
		query: string | undefined,
		stats: MentionStats | undefined,
	): void {
		const trimmedQuery = (query ?? '').trim();

		// Empty query: prepend recommended agents; inner provider returns people only.
		// Typed query: inner provider already returns agents (via appType) + people; pass through.
		const prefix =
			trimmedQuery.length === 0
				? this.getRecommendedAgents()
						.slice(0, MAX_RECOMMENDED_AGENT_RESULTS)
						.map(agentToMentionDescription)
				: [];

		const merged: MentionsResult = {
			mentions: [...prefix, ...innerMentions],
			query: query ?? '',
		};

		this._notifyListeners(merged, stats);
	}
}

const isAgentMention = (mention: MentionDescription): boolean =>
	mention.userType === 'APP' && mention.appType === 'agent';
